import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './CountyMap.css';
import { touchpointColorPalette } from '../utils/touchpointColors';

const LABEL_VISIBILITY_THRESHOLD = 2;
const INITIAL_ZOOM = 1;

function CountyMap({ geoData, onCountySelect, selectedCountyFips, countyData, selectedYear, touchpointTypes, showGlyphs }) {
  const svgRef = useRef();
  const containerRef = useRef();
  const zoomRef = useRef(null);
  const transformRef = useRef(null);
  const projectionRef = useRef(null);
  const pathRef = useRef(null);
  const svgD3Ref = useRef(null);
  const isInitializedRef = useRef(false);
  const zoomInRef = useRef(null);
  const zoomOutRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isRotated, setIsRotated] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!geoData || dimensions.width === 0) return;

    let width = dimensions.width;
    let height = dimensions.height;

    if (isRotated) {
      [width, height] = [height, width];
    }

    const svg = d3.select(svgRef.current);
    svgD3Ref.current = svg;
    svg.selectAll('*').remove();
    
    if (isRotated) {
      svg.attr('width', dimensions.height).attr('height', dimensions.width);
    } else {
      svg.attr('width', dimensions.width).attr('height', dimensions.height);
    }

    const projection = d3.geoMercator()
      .fitSize([width, height], geoData);
    projectionRef.current = projection;

    const path = d3.geoPath().projection(projection);
    pathRef.current = path;

    const g = svg.append('g');
    const labelsGroup = svg.append('g').attr('class', 'labels-group');
    const glyphsGroup = svg.append('g').attr('class', 'glyphs-group');

    const updateLabels = (transform) => {
      const currentZoom = transform.k;
      const shouldShowLabels = currentZoom >= LABEL_VISIBILITY_THRESHOLD;

      labelsGroup.attr('transform', transform);
      
      if (shouldShowLabels) {
        const fontSize = 9 / currentZoom;
        const labels = labelsGroup.selectAll('text')
          .data(geoData.features)
          .join('text')
          .attr('class', d => {
            const isSelected = selectedCountyFips && (parseInt(selectedCountyFips, 10) === parseInt(d.properties.id.substring(2), 10));
            return isSelected ? 'county-label county-label-selected' : 'county-label';
          })
          .text(d => d.properties.name)
          .attr('x', d => {
            const centroid = path.centroid(d);
            return centroid[0];
          })
          .attr('y', d => {
            const centroid = path.centroid(d);
            return centroid[1];
          })
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .style('pointer-events', 'none')
          .style('user-select', 'none')
          .style('font-size', `${fontSize}px`)
          .style('display', 'block');
        
        if (isRotated) {
          labels.attr('transform', d => {
            const centroid = path.centroid(d);
            return `translate(${centroid[0]},${centroid[1]}) rotate(90) translate(${-centroid[0]},${-centroid[1]})`;
          });
        } else {
          labels.attr('transform', null);
        }

        labels.attr('class', d => {
          const isSelected = selectedCountyFips && (parseInt(selectedCountyFips, 10) === parseInt(d.properties.id.substring(2), 10));
          return isSelected ? 'county-label county-label-selected' : 'county-label';
        });
      } else {
        labelsGroup.selectAll('text').style('display', 'none');
      }
    };

    const counties = g.selectAll('path')
      .data(geoData.features)
      .join('path')
      .attr('d', path)
      .attr('class', 'county')
      .attr('stroke', '#333')
      .attr('stroke-width', 0.5)
      .on('mouseenter', function(event, d) {
        const isSelected = selectedCountyFips && (parseInt(selectedCountyFips, 10) === parseInt(d.properties.id.substring(2), 10));
        if (!isSelected) {
          d3.select(this).attr('fill', '#aed6f1');
        }
      })
      .on('mouseleave', function(event, d) {
        const isSelected = selectedCountyFips && (parseInt(selectedCountyFips, 10) === parseInt(d.properties.id.substring(2), 10));
        d3.select(this).attr('fill', isSelected ? '#3498db' : '#e8f4f8');
      })
      .on('click', function(event, d) {
        onCountySelect(d);
      });
      
    counties.attr('fill', d => {
        const isSelected = selectedCountyFips && (parseInt(selectedCountyFips, 10) === parseInt(d.properties.id.substring(2), 10));
        return isSelected ? '#3498db' : '#e8f4f8';
    });

    counties.append('title')
      .text(d => d.properties.name);

    const updateGlyphs = (transform) => {
      if (!showGlyphs || !countyData || !selectedYear || !touchpointTypes) {
        glyphsGroup.selectAll('g.county-glyph').remove();
        return;
      }

      const maxPrevalence = 100;
      const maxRecency = 365;
      const radiusScale = d3.scaleLinear().domain([0, maxPrevalence]).range([2, 15]);
      const opacityScale = d3.scaleLinear().domain([0, maxRecency]).range([1, 0.2]);

      const angleStep = 360 / touchpointTypes.length;
      const touchpointAngles = {};
      touchpointTypes.forEach((type, i) => {
        touchpointAngles[type] = i * angleStep;
      });

      const glyphScale = 1 / Math.sqrt(transform.k);
      const glyphData = [];

      geoData.features.forEach(feature => {
        const countyFips = parseInt(feature.properties.id.substring(2), 10);
        const county = countyData[countyFips];
        if (!county) return;

        const totalDecedents = county.touchpoints['All']?.[selectedYear]?.['Decedent Total Individual Count'];
        if (!totalDecedents || totalDecedents === 0) return;

        const centroid = path.centroid(feature);
        const glyphInfo = {
          fips: countyFips,
          centroid: centroid,
          petals: []
        };

        touchpointTypes.forEach(type => {
          const touchpointDecedents = county.touchpoints[type]?.[selectedYear]?.['Decedent Touchpoint Individual Count'] || 0;
          const prevalence = (touchpointDecedents / totalDecedents) * 100;
          const recency = county.touchpoints[type]?.[selectedYear]?.['Average Days Since Most Recent Touchpoint Utilization'] || maxRecency;
          
          glyphInfo.petals.push({
            type,
            prevalence,
            recency,
            radius: radiusScale(prevalence),
            opacity: opacityScale(recency),
            angle: touchpointAngles[type]
          });
        });

        glyphData.push(glyphInfo);
      });

      const glyphGroups = glyphsGroup.selectAll('g.county-glyph')
        .data(glyphData, d => d.fips)
        .join('g')
        .attr('class', 'county-glyph')
        .attr('transform', d => {
          const [x, y] = d.centroid;
          return `translate(${x},${y}) scale(${glyphScale})`;
        })
        .style('pointer-events', 'none');

      glyphGroups.selectAll('path.petal').remove();
      
      glyphGroups.each(function(d) {
        const petalGroup = d3.select(this);
        d.petals.forEach(petal => {
          const startAngle = (petal.angle - angleStep / 2) * (Math.PI / 180);
          const endAngle = (petal.angle + angleStep / 2) * (Math.PI / 180);
          
          const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(petal.radius)
            .startAngle(startAngle)
            .endAngle(endAngle);

          petalGroup.append('path')
            .attr('class', 'petal')
            .attr('d', arc)
            .attr('fill', touchpointColorPalette[petal.type] || '#808080')
            .attr('fill-opacity', petal.opacity)
            .attr('stroke', '#fff')
            .attr('stroke-width', 0.5);
        });
      });
    };

    const zoom = d3.zoom()
      .scaleExtent([0.5, 8])
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        g.attr('transform', event.transform);
        glyphsGroup.attr('transform', event.transform);
        updateLabels(event.transform);
        updateGlyphs(event.transform);
      });

    zoomRef.current = zoom;
    svg.call(zoom);

    const centerX = width / 2;
    const centerY = height / 2;
    const initialTransform = d3.zoomIdentity
      .translate(centerX - centerX * INITIAL_ZOOM, centerY - centerY * INITIAL_ZOOM)
      .scale(INITIAL_ZOOM);

    transformRef.current = initialTransform;
    svg.call(zoom.transform, initialTransform);
    glyphsGroup.attr('transform', initialTransform);
    updateLabels(initialTransform);
    updateGlyphs(initialTransform);
    isInitializedRef.current = true;

    const zoomIn = () => {
      const centerX = width / 2;
      const centerY = height / 2;
      svg.transition().call(zoom.scaleTo, transformRef.current.k * 1.5, [centerX, centerY]);
    };

    const zoomOut = () => {
      const centerX = width / 2;
      const centerY = height / 2;
      svg.transition().call(zoom.scaleTo, transformRef.current.k / 1.5, [centerX, centerY]);
    };

    zoomInRef.current = zoomIn;
    zoomOutRef.current = zoomOut;

    return () => {
      svg.on('.zoom', null);
      isInitializedRef.current = false;
    };

  }, [geoData, onCountySelect, selectedCountyFips, dimensions.width, dimensions.height, isRotated, countyData, selectedYear, touchpointTypes, showGlyphs]);

  const prevSelectedCountyFipsRef = useRef(null);

  useEffect(() => {
    if (!selectedCountyFips || !geoData || !projectionRef.current || !pathRef.current || !svgD3Ref.current || !transformRef.current || !isInitializedRef.current) {
      prevSelectedCountyFipsRef.current = selectedCountyFips;
      return;
    }

    if (prevSelectedCountyFipsRef.current === selectedCountyFips) return;

    prevSelectedCountyFipsRef.current = selectedCountyFips;

    const svg = svgD3Ref.current;
    const path = pathRef.current;
    const width = parseFloat(svg.attr('width'));
    const height = parseFloat(svg.attr('height'));

    const selectedCounty = geoData.features.find(feature => {
      const countyFips = parseInt(feature.properties.id.substring(2), 10);
      return countyFips === parseInt(selectedCountyFips, 10);
    });

    if (!selectedCounty) return;

    const centroid = path.centroid(selectedCounty);
    const centerX = width / 2;
    const centerY = height / 2;

    const currentTransform = transformRef.current;
    const currentScale = currentTransform.k;

    const newTransform = d3.zoomIdentity
      .translate(centerX - centroid[0] * currentScale, centerY - centroid[1] * currentScale)
      .scale(currentScale);

    svg.transition()
      .duration(750)
      .call(zoomRef.current.transform, newTransform);

  }, [selectedCountyFips, geoData]);

  const rotationStyle = isRotated 
    ? { 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-90deg)',
        transformOrigin: 'center center'
      }
    : {};

  return (
    <div ref={containerRef} className="county-map-container">
      <svg 
        ref={svgRef} 
        className={`county-map ${isRotated ? 'rotated' : ''}`}
        style={rotationStyle}
      />
      <button 
        className="rotate-button"
        onClick={() => setIsRotated(!isRotated)}
        title={isRotated ? 'Rotate to vertical' : 'Rotate to horizontal'}
      >
        ↻
      </button>
      <button 
        className="zoom-button zoom-in-button"
        onClick={() => zoomInRef.current?.()}
        title="Zoom in"
      >
        +
      </button>
      <button 
        className="zoom-button zoom-out-button"
        onClick={() => zoomOutRef.current?.()}
        title="Zoom out"
      >
        −
      </button>
    </div>
  );
}

export default CountyMap;